import {AfterContentInit, Component, EventEmitter, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ApDynComponentComponent
}                                                                                from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPropertyColumnConfig,
  FilterType,
  PropertyTemplate
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridStatusColumnConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {combineLatest, Observable, of, Subscription}                             from 'rxjs';
import {FieldStore}                                                              from '../../stores/farm/field.store';
import {debounceTime, delay, map}                                                from 'rxjs/operators';
import {
  ApGetCropService
}                                                                                from '../../stores/services/ap-get-crop.service';
import {
  ApDynGridPagerConfig,
  IGridPagerCallBackData
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {ApGridPagerType, ApStatsType, MapViewCurrentMenu, MapViewMode}           from '../../ap-interface';
import {
  ApUtilService
}                                                                                from '../../ap-utils/service/ap-util.service';
import {
  NFertilizationStore
} from '../../stores/n-fertilization/n-fertilization.store';
import {
  ApIcons
} from '../../ap-interface/enums/layout/ap-icons';
import {
  CampaignYearStore
} from '../../stores/login/campaignyear.store';
import {
  GetRoundNumericService
}                                                                                from '../../ap-utils/service/get-round-numeric.service';
import {
  MapViewStore
}                                                                                from '../../stores/layout/mapview.store';
import {LoginStore}                                                              from '../../stores/login/login.store';
import {
  ApDateService
}                                                                                from '../../ap-core/services/ap-date-service';
import {
  ApTranslationService
}                                                                                from '../../ap-utils/service/ap-translation.service';
import {
  NotifyStore
}                                                                                from '../../stores/dialog/notify.store';
import {GetRoundNumericPipe}                                                     from '../../ap-utils';
import {
  SettingsStore
}                                                                                from '../../stores/base-data/settings.store';
import {
  INFertilizerNNeedGridItem
}                                                                                from '../types/ap-n-fertilization-n-need-types';
import {
  NFertilizationService
}                                                                                from '../../services/data/n-fertilization.service';
import {
  StatisticStore
}                                                                                from '../../stores/statistic/statistic.store';
import {
  ApDynGridGroupColumnConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  BasicFertilisationSummariesStore
}                                                                                from '../../stores/taskmanagement/basic.fertilisation.summaries.store';
import {
  StatisticService
}                                                                                from '../../services/data/statistic.service';
import IContentRow = Agriport.Excel.Factory.InputData.NRestriction.IContentRow;
import IReportData = Agriport.Excel.Factory.InputData.NRestriction.IReportData;
import ICropTypes = Data.BaseData.ICropTypes;
import IFieldCrop = Data.FieldManagement.IFieldCrop;
import IApValidationResult = Data.Api.Validation.IApValidationResult;
import ApValidationLevel = Data.Api.Validation.ApValidationLevel;

@Component({
  selector: 'ap-n-fertilization-p-need',
  template: `
    <ap-dyncomponent [caption]="'Global__N_Need'"
                     [canSearch]="true"
                     [loading$]="loading$"
                     [items]="items$ | async"
                     [headerIcon]="'ap-icon-n-fertilization'"
                     [columns]="columns"
                     [pdfExportTitle]="'Global__N_Need'"
                     [exportFileName]="exportFileName"
                     [pager]="pager">
    </ap-dyncomponent>
  `,
  styles: []
})
export class ApNFertilizationNNeedComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public items$: Observable<INFertilizerNNeedGridItem[]> = of([]);
  public loading$: Observable<boolean> = of(true);
  public columns: ApDynGridColumnConfigBase[] = [];
  public pager: ApDynGridPagerConfig;
  public exportFileName = '';

  private _statisticUpdateRequested = new EventEmitter<void>();
  private _subscriptions: Subscription[] = [];

  constructor(private campaignYearStore: CampaignYearStore,
              private fieldStore: FieldStore,
              private loginStore: LoginStore,
              private mapViewStore: MapViewStore,
              private notifyStore: NotifyStore,
              private settingsStore: SettingsStore,
              private statisticStore: StatisticStore,
              private nFertilizationStore: NFertilizationStore,
              private basicFertilisationSummariesStore: BasicFertilisationSummariesStore,
              private dateService: ApDateService,
              private getCropService: ApGetCropService,
              private statisticService: StatisticService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private nFertilizationService: NFertilizationService,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.fieldStore.Listen((s) => s.loading),
      this.nFertilizationStore.Listen((s) => s.logfilesLoading),
      this.nFertilizationStore.Listen(s => s.planBooksLoading),
      this.settingsStore.Listen(x => x.loading),
      this.basicFertilisationSummariesStore.Listen(x => x.loading)
    ]).pipe(
      map((loadingStatuses) => loadingStatuses.some(x => x))
    );
    this.fieldStore.changeSelectedField([]);
    this.columns = this._getColumns();
    this.pager = this._getPager();
    this.items$ = this._getItems();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(
      this.dynComponent.SelectedItems$.pipe(
        debounceTime(100),
        delay(1)
      ).subscribe(ids => this._selectionChange(ids))
    );
    this._subscriptions.push(
      this.nFertilizationStore.Listen(x => x.nRestrictionsReportDownloadSuccess).subscribe(downloadResult => {
        this._handleDownloadReportStatus(downloadResult);
      })
    );
    this._subscriptions.push(
      this._statisticUpdateRequested.pipe(debounceTime(100)).subscribe(_ => this._generateStats())
    );
    this._subscriptions.push(
      this.mapViewStore.Listen(s => s.currentMenu).subscribe(mapMenu => this._onMapMenuChanged(mapMenu))
    );
    this.nFertilizationStore.LoadNFertilizationPlanningSummaries();
    this.nFertilizationStore.LoadLogfiles();
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._statisticUpdateRequested?.unsubscribe();
    this.fieldStore.changeSelectedField([]);
    this.statisticStore.removeStatisticData();
  }

  private _getItems(): Observable<INFertilizerNNeedGridItem[]> {
    return combineLatest([
      this.fieldStore.Listen((s) => s.data),
      this.nFertilizationStore.Listen((s) => s.fertilizationNeedValue),
      this.nFertilizationStore.Listen(s => s.planBooks),
      this.nFertilizationStore.Listen(s => s.logfiles),
      this.basicFertilisationSummariesStore.Summaries$
    ]).pipe(
      map(([fields, fnvs, planBooks, logFiles, baseFertSummaries]) => {
        return fields.map(field => {
          const fieldCrop = this.getCropService.getFieldCrop(field);
          const fieldGeom = this.fieldStore.getCurrentFieldGeom(field);
          const mainCrop = this.getCropService.getCropType(fieldCrop, true);
          const secondCrop = this.getCropService.getCropType(fieldCrop, false);
          const needValue = fnvs.find((v) => v.FieldId === field.Id);
          const baseFertPlanBooks = baseFertSummaries.map(x => x.BasicFertilisationPlanBooks).flat();
          const alreadyApplied = this.nFertilizationService.calculateAlreadyAppliedFromContentNPlanBooks(field, planBooks, logFiles, baseFertPlanBooks);
          const nBalance = this._getNBalanceDifference(alreadyApplied, fieldCrop, mainCrop, secondCrop);
          const removal = this._getNBalance(fieldCrop, mainCrop, secondCrop);
          const remaining = this.nFertilizationService.calculateRemainingValue(alreadyApplied, needValue?.Value);
          return {
            Id: field.Id,
            FnvId: needValue?.Id,
            Status: 1,
            StatusClass: 'ap-status-green',
            StatusDesc: 'Global__Active',
            MainCropType: mainCrop?.Description ?? '',
            Nr: field.FieldNumber,
            SNr: field.FieldSubnumber,
            NrSNr: this.fieldStore.getFieldNumberAndSubNumber(field),
            Name: field.FieldName,
            Area: fieldGeom?.AreaHa ?? 0,
            Removal: removal ? removal * -1 : 0,
            MainCropExpectedYield: fieldCrop?.ExpectedYield,
            AlreadyApplied: alreadyApplied ?? 0,
            Balance: nBalance ?? 0,
            MaxAllowance: needValue?.Value ?? 0,
            Remaining: remaining ?? 0
          } as INFertilizerNNeedGridItem;
        });
      })
    );
  }

  private _getColumns(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: 'Status',
        class: 'StatusClass',
        description: 'StatusDesc'
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Nr',
        title: 'Global__NumberAbbr',
        width: 60,
        filterable: true,
        headerFilterable: true,
        hide: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right'
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SNr',
        title: 'Global__FieldPartShort',
        width: 60,
        filterable: true,
        headerFilterable: true,
        hide: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right'
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__NumberAbbr',
        field: 'NrSNr',
        filterable: true,
        headerFilterable: true,
        hide: new ApDynGridColumnHideConfig({
          mapHide: false,
          selfHide: this.mapViewStore.Listen(s => s.mode).pipe(map((m) => m !== MapViewMode.NORMAL)),
        }),
        width: 75
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Name',
        title: 'Global__Name',
        cssClass: 'left',
        width: 180,
        filterable: true,
        headerFilterable: true,
        sortIndex: 1
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Area',
        title: 'Global_ha_Unit',
        width: 60,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        filterable: true,
        headerFilterable: true,
        template: PropertyTemplate.NUMBER,
        tooltip: dataItem => dataItem.Area,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingsStore.FirstSetting.DigitsAfterDecimalPoint, {ToReplace: [0], ReplaceWith: ''}]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'MainCropType',
        title: 'Global__Crop',
        width: 100,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'MainCropExpectedYield',
        title: 'Nutrients_Pages_Popups__YieldUnit',
        width: 60,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        template: PropertyTemplate.NUMBER,
        filterable: true,
        hide: true,
        tooltip: dataItem => dataItem.MainCropExpectedYield,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0, {ToReplace: [0], ReplaceWith: ''}]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Nutrients__kg_N_per_ha',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: 'Removal',
            title: 'Settings__Label_Detraction',
            width: 60,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            template: PropertyTemplate.NUMBER,
            filterable: true,
            headerFilterable: true,
            tooltip: dataItem => dataItem.Removal,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'AlreadyApplied',
            title: 'Nutrients__already_applied',
            width: 60,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            template: PropertyTemplate.NUMBER,
            filterable: true,
            headerFilterable: true,
            tooltip: dataItem => dataItem.AlreadyApplied,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'Balance',
            title: 'Global__Balance',
            width: 60,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            template: PropertyTemplate.NUMBER,
            filterable: true,
            headerFilterable: true,
            tooltip: dataItem => dataItem.Balance,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'MaxAllowance',
            title: 'Nutrients__Need_FNV',
            width: 60,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            filterable: true,
            headerFilterable: true,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [this.settingsStore.FirstSetting.DigitsAfterDecimalPoint, {ToReplace: [0], ReplaceWith: ''}]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'Remaining',
            title: 'Nutrients__Remaining',
            width: 60,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            filterable: true,
            headerFilterable: true,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [this.settingsStore.FirstSetting.DigitsAfterDecimalPoint, {ToReplace: [0], ReplaceWith: ''}]
            }]
          })
        ]
      })
    ];
  }

  private _getPager(): ApDynGridPagerConfig {
    return new ApDynGridPagerConfig({
      listMassEditing: [
        {
          id: 0,
          name: 'Nutrients__Set_FNV',
          type: ApGridPagerType.Numeric,
          onSubmit: ApUtilService.createEventEmitter(this._setFertilizationNeedValue.bind(this), this._subscriptions),
          placeholder: 'Nutrients__kg_N_per_ha'
        }
      ],
      iconButtons: [
        {
          icon: ApIcons.PDF,
          callback: ApUtilService.createEventEmitter(this._exportAsPdf.bind(this), this._subscriptions),
          tooltip: 'Global__Save_PDF'
        },
        {
          icon: ApIcons.XLS,
          callback: ApUtilService.createEventEmitter(this._exportAsXls.bind(this), this._subscriptions),
          tooltip: 'Global__Save_EXCEL'
        },
      ]
    });
  }

  private _exportAsPdf(): void {
    this.exportFileName = this._getExportFilename();
    this.dynComponent.dynGrid.exportAsPdf();
  }

  private _exportAsXls(): void {
    const contentRows: IContentRow[] = [];
    const rows = this.dynComponent.getSelectedItems();
    rows.forEach(row => {
      const a = row as INFertilizerNNeedGridItem;
      if (a) {
        contentRows.push({
          Number: a.Nr.toString(),
          SubNumber: a.SNr.toString(),
          Name: a.Name,
          Area: this.roundNumericService.round(a.Area),
          MainCropType: a.MainCropType,
          MainCropExpectedYield: this.roundNumericPipe.transform(a.MainCropExpectedYield?.toString(), 0, {
            ToReplace: [0],
            ReplaceWith: ''
          }),
          Removal: this.roundNumericService.round(a.Removal, 0),
          AlreadyApplied: this.roundNumericService.round(a.AlreadyApplied, 0),
          Balance: this.roundNumericService.round(a.Balance, 0),
          MaxAllowance: this.roundNumericPipe.transform(a.MaxAllowance.toString(), 0, {
            ToReplace: [0],
            ReplaceWith: ''
          }),
          Remaining: this.roundNumericPipe.transform(a.Remaining.toString(), 0, {ToReplace: [0], ReplaceWith: ''})
        });
      }
    });

    const columns = this._getReportColumnNames();
    const reportData: IReportData = {
      Content: {
        HeaderLabels: [
          columns.get('Number'),
          columns.get('SubNumber'),
          columns.get('Name'),
          columns.get('Area'),
          columns.get('MainCropType'),
          columns.get('MainCropExpectedYield'),
          columns.get('Removal'),
          columns.get('AlreadyApplied'),
          columns.get('Balance'),
          columns.get('MaxAllowance'),
          columns.get('Remaining'),
        ],
        Rows: contentRows,
      },
      Header: {
        Number: columns.get('Number'),
        SubNumber: columns.get('SubNumber'),
        Name: columns.get('Name'),
        Area: columns.get('Area'),
        MainCropType: columns.get('MainCropType'),
        MainCropExpectedYield: columns.get('MainCropExpectedYield'),
        Removal: columns.get('Removal'),
        AlreadyApplied: columns.get('AlreadyApplied'),
        Balance: columns.get('Balance'),
        MaxAllowance: columns.get('MaxAllowance'),
        Remaining: columns.get('Remaining')
      }
    };
    const bookName = this._getExportFilename();
    this.nFertilizationStore.downloadNRestrictionReport(bookName, `${bookName}.xlsx`, reportData);
  }

  private _getExportFilename(): string {
    const nBalanceTitle = this.translationService.translate('Global__N_Need').replace(' ', '_');
    const customerExpr = `${this.loginStore.SelectedFarm?.Country?.Id}${this.loginStore.SelectedFarm?.CustomerNumber}`;
    const dateExpr = this.dateService.toFarmDate(new Date()).format('YYMMDD');
    return `${dateExpr}_${nBalanceTitle}_${customerExpr}`;
  }

  private _selectionChange(items: INFertilizerNNeedGridItem[]): void {
    this.fieldStore.changeSelectedField(items.filter(x => x.Id).map(x => x.Id as string));
    this._statisticUpdateRequested.emit();
  }

  private _onMapMenuChanged(mapMenu: MapViewCurrentMenu): void {
    if (mapMenu === MapViewCurrentMenu.STATS) {
      this._statisticUpdateRequested.emit();
    }
  }

  private _generateStats(): void {
    if (this.mapViewStore.getMapViewCurrentMenu() !== MapViewCurrentMenu.STATS) {
      return;
    }
    // put it on top of callstack to avoid lagging when user changes the selection
    setTimeout(() => {
      const emptyTitle = `${this.translationService.translate('Nutrients__StatisticsTitle')}
      ${this.translationService.translate('Global__N_Need')}`;
      let items = this.dynComponent.getSelectedItems() as INFertilizerNNeedGridItem[];
      if (!items.Any()) {
        items = this.dynComponent.items as INFertilizerNNeedGridItem[];
      }
      if (!items?.Any()) {
        this.statisticStore.setStatisticData([], ApStatsType.NFertilizationNNeedStats, '', emptyTitle);
        return;
      }
      const title = this._getTitleForStatistic(items);
      this.statisticStore.setStatisticData<INFertilizerNNeedGridItem>(items, ApStatsType.NFertilizationNNeedStats, title, emptyTitle);
    }, 0);
  }

  private _getTitleForStatistic(selectedItems: INFertilizerNNeedGridItem[]): string {
    const totalItems = this.dynComponent.items as INFertilizerNNeedGridItem[];
    const dataForTitles = totalItems.map(x => ({
      AreaHa: x.Area,
      Selected: selectedItems.some(z => z.Id === x.Id)
    }));
    return this.statisticService.generateFieldsAreaTitle(dataForTitles);
  }

  private _handleDownloadReportStatus(downloadResult: boolean | undefined): void {
    if (downloadResult === undefined) {
      return;
    }
    const notification = {
      DisplayTimeout: 5,
      Level: ApValidationLevel.Success,
      ErrorKey: 'Global__Report_DownloadSuccess'
    } as IApValidationResult;
    if (!downloadResult) {
      notification.Level = ApValidationLevel.Error;
      notification.ErrorKey = 'Global__Report_DownloadFail';
    }
    this.notifyStore.addMessage(notification);
  }

  private _getReportColumnNames(): Map<string, string> {
    const columnNames = new Map<string, string>();
    columnNames.set('Number', this.translationService.translate('Global__NumberAbbr'));
    columnNames.set('SubNumber', this.translationService.translate('Global__FieldPartShort'));
    columnNames.set('Name', this.translationService.translate('Global__Name'));
    columnNames.set('Area', this.translationService.translate('Global_ha_Unit'));
    columnNames.set('MainCropType', this.translationService.translate('Global__Crop'));
    columnNames.set('MainCropExpectedYield', this.translationService.translate('Nutrients_Pages_Popups__YieldUnit'));
    columnNames.set('Removal', this.translationService.translate('Settings__Label_Detraction'));
    columnNames.set('AlreadyApplied', this.translationService.translate('Nutrients__already_applied'));
    columnNames.set('Balance', this.translationService.translate('Global__Balance'));
    columnNames.set('MaxAllowance', this.translationService.translate('Nutrients__Need_FNV'));
    columnNames.set('Remaining', this.translationService.translate('Nutrients__Remaining'));
    return columnNames;
  }

  private _getNBalanceDifference(alreadyApplied: number | null, fieldCrop: IFieldCrop | null, mainCrop: ICropTypes | null, secondCrop: ICropTypes | null): number | null {
    const nBalance = this._getNBalance(fieldCrop, mainCrop, secondCrop);
    if (nBalance == null) {
      return null;
    }
    if (alreadyApplied === null) {
      alreadyApplied = 0;
    }
    alreadyApplied -= nBalance;
    return alreadyApplied;
  }

  private _getNBalance(fieldCrop: IFieldCrop | null, mainCrop: ICropTypes | null, secondCrop: ICropTypes | null): number | null {
    if (!fieldCrop || !mainCrop || fieldCrop.ExpectedYield <= 0) {
      return null;
    }
    let result = mainCrop.MainwdN * fieldCrop.ExpectedYield;
    if (secondCrop && fieldCrop.ExpectedYieldSecond) {
      result += secondCrop.MainwdN * fieldCrop.ExpectedYieldSecond;
    }
    if (fieldCrop.StrawHarvested && fieldCrop.ExpectedYield) {
      result += mainCrop.SecwdN * mainCrop.ExpectedYield * mainCrop.MsRatio;
    }
    return result;
  }

  private _setFertilizationNeedValue(data: IGridPagerCallBackData): void {
    this.nFertilizationStore.setFertilizationNeedValue(data.items.map(item => ({
      Id: item['FnvId'],
      FieldId: item.Id,
      CampaignYear: this.campaignYearStore.getSelectedCampaignYear().Year,
      Value: this.roundNumericService.roundAsNumber(data.value, 0)
    })));
  }
}
